import { Dispatch } from "redux";
import { wsURL } from "../../api/api";
import {
    setTextToSpeech,
    loading,
    seterror,
    resetTextToSpeech,
    setGeneratorId,
} from "../reducers/getTextToSpeechReducer";

/**
 * Lesson audio (text-to-speech) over `/ws/lesson_audio/<lesson_id>/`.
 *
 * Replaces the old `POST /lessons/text-to-speech/` call. The socket is opened
 * per request, `{"action":"generate"}` is sent on open, and `{"action":"status"}`
 * is polled every 15s until a frame carrying an `audio_url` lands (generation
 * takes 30-90s, so the server also pushes `status`/`heartbeat` frames in
 * between — those are ignored, the polling is what resolves the request).
 *
 * Generation outlives a single connection: the backend keys the job by lesson,
 * so a dropped socket is reconnected and re-polled instead of failing the
 * request. Only an explicit error frame, an auth close or the overall ceiling
 * ends it unsuccessfully.
 *
 * The reducer shape is unchanged, so every consumer keeps reading
 * `texttospeech.voice.audio_url` / `.generatorId` / `.isLoading` / `.error`.
 */

const POLL_INTERVAL_MS = 15000;
// Hard ceiling so a stuck generation can never leave the player spinning forever.
const MAX_WAIT_MS = 5 * 60 * 1000;
const MAX_RECONNECT_ATTEMPTS = 5;
const RECONNECT_DELAY_MS = 2000;
const UNAUTHORIZED_CLOSE_CODE = 4401;

type Mode = "generator" | "lesson";

interface LessonAudioParams {
    /** HTML/plain text to read out. */
    text: string;
    /** "generator" = teacher lesson generator, "lesson" = student lesson tab. */
    mode: Mode;
    /** Chat message uuid — required for mode "generator". */
    generatorId?: string;
    /** Lesson pk — required for mode "lesson". */
    lessonId?: number | string;
    /** Lesson pk used as the socket path segment. The URL only ever carries a
     *  lesson id, so the generator passes the lesson it is editing. Defaults to
     *  `lessonId`. */
    socketLessonId?: number | string;
    token: string;
}

// Only one audio request can be in flight at a time (the UI only ever shows one
// player), so the socket and its timers live at module scope and are torn down
// before a new request starts. `activeSession` lets late frames from a
// superseded request be ignored.
let activeSession: { active: boolean } | null = null;
let activeSocket: WebSocket | null = null;
let pollTimer: ReturnType<typeof setInterval> | null = null;
let timeoutTimer: ReturnType<typeof setTimeout> | null = null;
let reconnectTimer: ReturnType<typeof setTimeout> | null = null;

const clearPollTimer = () => {
    if (pollTimer) {
        clearInterval(pollTimer);
        pollTimer = null;
    }
};

const clearTimers = () => {
    clearPollTimer();
    if (timeoutTimer) {
        clearTimeout(timeoutTimer);
        timeoutTimer = null;
    }
    if (reconnectTimer) {
        clearTimeout(reconnectTimer);
        reconnectTimer = null;
    }
};

/** Detaches the handlers first so our own close never reports as a failure or
 *  triggers a reconnect. */
const closeSocket = (socket: WebSocket | null) => {
    if (!socket) return;

    socket.onopen = null;
    socket.onmessage = null;
    socket.onerror = null;
    socket.onclose = null;

    try {
        if (
            socket.readyState === WebSocket.OPEN ||
            socket.readyState === WebSocket.CONNECTING
        ) {
            socket.close();
        }
    } catch (error) {
        console.error("Lesson audio socket: close failed", error);
    }

    if (activeSocket === socket) activeSocket = null;
};

/** Aborts any in-flight request. Safe to call when nothing is running. */
const stopLessonAudio = () => {
    if (activeSession) activeSession.active = false;
    activeSession = null;
    clearTimers();
    closeSocket(activeSocket);
};

/**
 * The ready frame is `{"type":"audio","status":"ready","audio_url":"…"}`, but a
 * cached hit can come back on a plain `status` frame and the key has moved
 * around between backend revisions. Anything that hands us a usable URL counts
 * as ready — the `type`/`status` labels are not worth failing a request over.
 */
const extractAudioUrl = (frame: any): string | undefined => {
    if (!frame || typeof frame !== "object") return undefined;

    const candidates = [
        frame.audio_url,
        frame.audioUrl,
        frame.url,
        frame.data?.audio_url,
        frame.message?.audio_url,
        frame.voice?.audio_url,
    ];

    return candidates.find(
        (candidate) => typeof candidate === "string" && candidate.length > 0
    );
};

const getLessonAudio =
    ({
        text,
        mode,
        generatorId,
        lessonId,
        socketLessonId,
        token,
    }: LessonAudioParams) =>
        (dispatch: Dispatch) => {
            // The socket path is keyed by lesson id only — never a generator uuid.
            const pathLessonId = socketLessonId ?? lessonId;

            if (!pathLessonId || !token) {
                dispatch(seterror("Unable to generate audio for this lesson."));
                return;
            }

            // A second play click supersedes whatever is still running.
            stopLessonAudio();
            dispatch(loading(true));

            const session = { active: true };
            activeSession = session;

            // The id the UI matches the player against. Always the id we asked
            // with — the server echo is informational and has no bearing on
            // which chat bubble the player belongs to.
            const ownerId = mode === "generator" ? generatorId : lessonId;

            const generatePayload =
                mode === "generator"
                    ? { action: "generate", mode, generator_id: generatorId, text }
                    : { action: "generate", mode, lesson_id: lessonId, text };

            const statusPayload =
                mode === "generator"
                    ? { action: "status", mode, generator_id: generatorId }
                    : { action: "status", mode, lesson_id: lessonId };

            const url = `${wsURL}ws/lesson_audio/${pathLessonId}/?token=${token}`;

            let reconnectAttempts = 0;
            let hasRequestedGenerate = false;
            // Diagnostic only — tells a silent close apart from a rejected URL.
            let hasReceivedFrame = false;

            const finish = () => {
                session.active = false;
                if (activeSession === session) activeSession = null;
                clearTimers();
                closeSocket(activeSocket);
            };

            const finishWithAudio = (audioUrl: string) => {
                if (!session.active) return;
                finish();
                // Order matters: `voice` has to be in the store before
                // `generatorId` flips, otherwise the player renders against a
                // null `voice`.
                dispatch(setTextToSpeech({ audio_url: audioUrl } as any));
                dispatch(setGeneratorId(ownerId as any));
            };

            const finishWithError = (message: string) => {
                if (!session.active) return;
                finish();
                dispatch(seterror(message));
            };

            const connect = () => {
                if (!session.active) return;

                let socket: WebSocket;

                try {
                    socket = new WebSocket(url);
                } catch (error) {
                    console.error("Lesson audio socket: connection failed", error);
                    finishWithError("Unable to connect to the audio service.");
                    return;
                }

                activeSocket = socket;

                socket.onopen = () => {
                    if (!session.active) return;

                    // `generate` is only ever sent once per request. A resumed
                    // connection asks for status so the running job is not
                    // restarted from scratch.
                    if (hasRequestedGenerate) {
                        socket.send(JSON.stringify(statusPayload));
                    } else {
                        socket.send(JSON.stringify(generatePayload));
                        hasRequestedGenerate = true;
                    }

                    clearPollTimer();
                    pollTimer = setInterval(() => {
                        if (socket.readyState === WebSocket.OPEN) {
                            socket.send(JSON.stringify(statusPayload));
                        }
                    }, POLL_INTERVAL_MS);
                };

                socket.onmessage = (event: MessageEvent) => {
                    if (!session.active) return;

                    hasReceivedFrame = true;

                    let frame: any;

                    try {
                        frame = JSON.parse(event.data);
                    } catch (error) {
                        console.error(
                            "Lesson audio socket: unparseable frame",
                            event.data,
                            error
                        );
                        return;
                    }

                    if (frame?.type === "error") {
                        console.error("Lesson audio socket: error frame", frame);
                        finishWithError(frame?.message ?? "Failed to generate audio.");
                        return;
                    }

                    const audioUrl = extractAudioUrl(frame);

                    if (audioUrl) {
                        finishWithAudio(audioUrl);
                        return;
                    }

                    // "status" | "heartbeat" | "pong" — still generating, keep polling.
                };

                socket.onerror = (error) => {
                    // onclose always follows, so the outcome is decided there.
                    console.error("Lesson audio socket error", error);
                };

                socket.onclose = (event: CloseEvent) => {
                    if (!session.active) return;

                    console.warn("Lesson audio socket closed", {
                        code: event.code,
                        reason: event.reason,
                        receivedAnyFrame: hasReceivedFrame,
                    });

                    clearPollTimer();
                    if (activeSocket === socket) activeSocket = null;

                    if (event.code === UNAUTHORIZED_CLOSE_CODE) {
                        finishWithError(
                            "Your session has expired. Please refresh and try again."
                        );
                        return;
                    }

                    // The job keeps running server-side, so a dropped connection
                    // is resumed rather than failed.
                    if (reconnectAttempts >= MAX_RECONNECT_ATTEMPTS) {
                        finishWithError(
                            "The audio connection closed before the audio was ready."
                        );
                        return;
                    }

                    reconnectAttempts += 1;
                    reconnectTimer = setTimeout(connect, RECONNECT_DELAY_MS);
                };
            };

            timeoutTimer = setTimeout(
                () =>
                    finishWithError(
                        "Audio is taking too long to generate. Please try again."
                    ),
                MAX_WAIT_MS
            );

            connect();
        };

/** Aborts the in-flight request and clears the reducer. Use on unmount. */
const resetLessonAudio = () => (dispatch: Dispatch) => {
    stopLessonAudio();
    dispatch(resetTextToSpeech());
};

export { getLessonAudio, resetLessonAudio, stopLessonAudio };
